import * as React from "react";
import { Alert, Tag } from "antd";
import { RefreshCw } from "react-feather";
import { linksOf } from "../../../services/cpln";
import { useGetMk8sNodesQuery } from "../../../store/api/mk8sApi";
import { MK8S_NODE_STATUS_INTERVAL_MS } from "../../../envVariables";
import { Mk8sCluster } from "../../../schema/types/mk8s/entity/mk8s";
import { mk8sHelpers } from "../../../mst/kinds/mk8s.helpers";
import { Table } from "../../../newcomponents/table/table";
import { ViewModal } from "../../../components/modals/viewModal";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { useCpFormValue } from "../../gvc/testNewForm/utils";
import { ProcessedNode, processNodes } from "./nodeUtils";
import { getNodeColumns } from "./nodeColumns";

interface Props {
  mk8s: Mk8sCluster;
}
export const Mk8sNodes: React.FC<Props> = ({ mk8s }) => {
  const selfLink = linksOf(mk8s).self!;

  const { data: nodes, isLoading, isError, error, refetch, isFetching } = useGetMk8sNodesQuery(selfLink, {
    pollingInterval: MK8S_NODE_STATUS_INTERVAL_MS,
  });

  const [selectedNode, setSelectedNode] = React.useState<ProcessedNode | null>(null);

  const basePath = React.useContext(BasePathContext);
  const { id: formId } = useCpFormContext();
  const provider = mk8sHelpers.getProvider(mk8s);
  const formNodePools: { id?: string; name: string }[] = useCpFormValue(`${provider}.nodePools`) || [];

  const nodePoolNames = React.useMemo(() => {
    return new Set<string>(formNodePools.map((p) => p.name));
  }, [formNodePools]);

  const nodePoolIdMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const p of formNodePools) {
      if (p.id && p.name) map.set(p.name, p.id);
    }
    return map;
  }, [formNodePools]);

  const processedNodes = React.useMemo(() => processNodes(nodes || []), [nodes]);

  const statusCounts = React.useMemo(() => {
    const counts = { ready: 0, warning: 0, error: 0 };
    for (const node of processedNodes) {
      if (node.statusLabel === "Ready") counts.ready++;
      else if (node.statusLabel.startsWith("Ready,")) counts.warning++;
      else counts.error++;
    }
    return counts;
  }, [processedNodes]);

  const onNodeClick = React.useCallback((node: ProcessedNode) => setSelectedNode(node), []);

  const columns = React.useMemo(
    () => getNodeColumns({ provider, nodePoolNames, basePath, formId, nodePoolIdMap, onNodeClick }),
    [provider, nodePoolNames, basePath, formId, nodePoolIdMap, onNodeClick],
  );

  if (isError) {
    const errMessage =
      error && typeof error === "object" && "data" in error
        ? typeof error.data === "string"
          ? error.data
          : "Failed to fetch node status"
        : "Failed to fetch node status";

    return (
      <div className="cpln-form-section cpln-ant-card-container">
        <Alert type="error" showIcon message="Error Loading Nodes" description={errMessage} />
      </div>
    );
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Table<ProcessedNode>
        tableId="mk8s-nodes"
        data={processedNodes}
        columns={columns}
        isLoading={isLoading}
        title={(className) => (
          <div className={`flex items-center gap-3 ${className}`}>
            <span>Nodes</span>
            {!isLoading && processedNodes.length > 0 ? (
              <div className="flex items-center gap-2">
                {statusCounts.ready > 0 ? (
                  <Tag color="success">{statusCounts.ready} Ready</Tag>
                ) : null}
                {statusCounts.warning > 0 ? (
                  <Tag color="warning">{statusCounts.warning} Warning</Tag>
                ) : null}
                {statusCounts.error > 0 ? (
                  <Tag color="error">{statusCounts.error} Not Ready</Tag>
                ) : null}
              </div>
            ) : null}
            <button
              type="button"
              className="color-link-hover cursor-pointer"
              style={{ background: "none", border: "none", padding: 4, display: "flex", alignItems: "center" }}
              onClick={() => refetch()}
              title="Refresh"
            >
              <RefreshCw style={{ width: 14, height: 14 }} className={isFetching ? "animate-spin" : ""} />
            </button>
          </div>
        )}
        styles={{ container: { width: "100%" } }}
      />
      {selectedNode ? (
        <ViewModal
          object={selectedNode.node}
          title={selectedNode.name}
          filename={`mk8s-${mk8s.name}-node-${selectedNode.name}`}
          visible={!!selectedNode}
          onClose={() => setSelectedNode(null)}
          initialFormat="yaml"
          noSlim
          width={1000}
        />
      ) : null}
    </div>
  );
};
